import { getLimitedVal } from 'utils';

export const CALENDLY_URL = {
    DEMO: 'https://calendly.com/therapylake/demo',
    SUPPORT_TRAINING: 'https://calendly.com/therapylake/support-training',
};

/**
 * Max length of a prior authorization number.
 *
 * Bound by X12 005010X222A1 loop 2300 REF02 (Prior Authorization Number), AN 1/50.
 * Payer-assigned auth numbers must be entered exactly as issued — including any
 * hyphens — so this caps length only, never character set.
 *
 * DUPLICATED from MAX_AUTHORIZATION_ID_LENGTH in @therapylake/package
 * (authorizedService.constants.ts), which backs the server-side @MaxLength.
 * It cannot be imported: the client's `prebuild` runs package's `build:frontend`,
 * whose tsconfig.frontend.json includes only `src/sentry-shared/**`, so
 * `@therapylake/package/types` is never emitted and will not resolve in CI.
 * Keep the two values in sync until the frontend build surface is widened.
 */
export const MAX_AUTHORIZATION_ID_LENGTH = 50;

/**
 * Org-wide minute step of every `TimeInput` (Settings → System Preferences): the picker lists only
 * multiples of it; typing any minute is still allowed. Read from `company.timeInputMinuteStep` (stored in org-settings).
 * DUPLICATED from TimeInputMinuteStep in @therapylake/package (company.constants.ts) — same reason as above.
 */
export const TimeInputMinuteStep = {
    REGULAR: 1,
    FIVE: 5,
    FIFTEEN: 15,
    THIRTY: 30,
};

export const timeInputMinuteStepOptions = [
    { value: TimeInputMinuteStep.REGULAR, label: 'Regular' },
    { value: TimeInputMinuteStep.FIVE, label: '5 min' },
    { value: TimeInputMinuteStep.FIFTEEN, label: '15 min' },
    { value: TimeInputMinuteStep.THIRTY, label: '30 min' },
];

/**
 * Where an appt location / client scheduling address comes from (`type` on GET /appt/addresses
 * entries and on the client scheduling address).
 * DUPLICATED from ApptAddressType in @therapylake/package (appt.enums.ts) — same reason as above.
 */
export const ApptAddressType = {
    CLIENT: 'client',
    CLIENT_CONTACT: 'clientContact',
    STAFF: 'staff',
    OFFICE: 'office',
};

export const apptAddressTypeLabels = {
    [ApptAddressType.CLIENT]: 'Client',
    [ApptAddressType.CLIENT_CONTACT]: 'Client Contact',
    [ApptAddressType.STAFF]: 'Staff',
    [ApptAddressType.OFFICE]: 'Office',
};

/** Dropdown group headers, in the order the backend returns the groups. */
export const apptAddressGroupLabels = {
    [ApptAddressType.CLIENT]: 'Client Addresses',
    [ApptAddressType.CLIENT_CONTACT]: 'Client Contact Addresses',
    [ApptAddressType.STAFF]: 'Staff',
    [ApptAddressType.OFFICE]: 'Company / Office',
};

/** "Home · 123 Main St, …" when there is a label, otherwise just the address. */
export const renderLabeledAddress = (label, formattedAddress) =>
    [label?.trim(), formattedAddress].filter(Boolean).join(' · ');

export const list = [
    { name: 'ACTIVE', id: 'ACTIVE' },
    { name: 'INACTIVE', id: 'INACTIVE' },
    { name: 'HOLD', id: 'HOLD' },
    { name: 'TERMINATED', id: 'TERMINATED' },
];

export const apptIssueType = {
    INCOMPLETE_UNITS: 'INCOMPLETE_UNITS',
    EXCEEDED_UNITS: 'EXCEEDED_UNITS',
    AUTHORIZATION_EXPIRED: 'AUTHORIZATION_EXPIRED',
    UNIT_OUT_OF_RANGE: 'UNIT_OUT_OF_RANGE',
    FREQUENCY_LIMIT_EXCEEDED: 'FREQUENCY_LIMIT_EXCEEDED',
    TB_DOJ_ERROR: 'TB_DOJ_ERROR',
    DIAGNOSTIC_CODE_MISSING: 'DIAGNOSTIC_CODE_MISSING',
    /** Enforced payer service + no accepted client diagnosis. Blocks billing, no bypass permission. */
    DIAGNOSIS_MISMATCH: 'DIAGNOSIS_MISMATCH',
};

export const apptErrors = [
    { id: apptIssueType.INCOMPLETE_UNITS, name: 'Incomplete units' },
    { id: apptIssueType.EXCEEDED_UNITS, name: 'Exceeded units' },
    { id: apptIssueType.AUTHORIZATION_EXPIRED, name: 'Authorization expired' },
    { id: apptIssueType.UNIT_OUT_OF_RANGE, name: 'Unit out of range' },
    { id: apptIssueType.FREQUENCY_LIMIT_EXCEEDED, name: 'Frequency limit exceeded' },
    { id: apptIssueType.TB_DOJ_ERROR, name: 'Tb/Doj Error' },
    { id: apptIssueType.DIAGNOSTIC_CODE_MISSING, name: 'Missing diagnostic code', diagnosisMatchOnly: true },
    { id: apptIssueType.DIAGNOSIS_MISMATCH, name: 'Diagnosis mismatch', diagnosisMatchOnly: true },
];

/** "Errored" filter options. The diagnosis ones are listed only while the org's diagnosis matching is on. */
export const getApptErrorOptions = (diagnosisMatchEnabled) =>
    apptErrors.filter((option) => diagnosisMatchEnabled || !option.diagnosisMatchOnly);

export const formatIssueType = (issueType) => {
    const formatted = issueType.toLowerCase().replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * File upload purposes — must match the KeyPurpose enum in
 * @therapylake/package/storage. The string values are the second path
 * segment in S3 keys: `{orgId}/{purpose}/{uuid}.{ext}`.
 */
/**
 * Cap on the AI generation instructions. Must match `FormGenerationLimits.MAX_PROMPT`
 * in @therapylake/package — the api rejects anything longer, and the textarea is
 * deliberately left uncapped so the author can see how far over they are.
 */
export const INSTRUCTIONS_MAX_LENGTH = 10000;

export const KeyPurpose = {
    AVATAR: 'avatars',
    SIGNATURE: 'signatures',
    CLINICAL_DOC: 'clinical-docs',
    CLINICAL_VIDEO: 'clinical-videos',
    TUTORIAL_VIDEO: 'tutorial-videos',
    TUTORIAL_THUMBNAIL: 'tutorial-thumbnails',
    CLAIM_DOC: 'claim-docs',
    INVOICE_DOC: 'invoice-docs',
    AUTH_DOC: 'auth-docs',
    FUNDER_DOC: 'funder-docs',
    TIMESHEET_DOC: 'timesheet-docs',
    RESOURCE_DOC: 'resource-docs',
    REPORT_FILE: 'report-files',
    GOAL_FILE: 'goal-files',
    CHAT_ATTACHMENT: 'chat-attachments',
    REMITTANCE_DOC: 'remittance-docs',
    STAFF_DOC: 'staff-docs',
    FORM_AI_SOURCE: 'form-ai-sources',
    WISHLIST_ATTACHMENT: 'wishlist-attachments',
    LOGO: 'logos',
    QR: 'qr',
};

/**
 * Per-purpose content-type allowlist. Must stay in sync with
 * @therapylake/package storage.constants.ts.
 */
export const CONTENT_TYPE_ALLOWLIST_BY_PURPOSE = {
    [KeyPurpose.AVATAR]: ['image/png', 'image/jpeg', 'image/webp'],
    [KeyPurpose.SIGNATURE]: ['image/png', 'image/jpeg', 'image/webp'],
    [KeyPurpose.LOGO]: ['image/png', 'image/jpeg', 'image/webp'],

    [KeyPurpose.CLINICAL_DOC]: [
        'image/png',
        'image/jpeg',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    // PDF only — a Word document is a ZIP of XML no model reads natively.
    [KeyPurpose.FORM_AI_SOURCE]: ['application/pdf'],
    [KeyPurpose.CLAIM_DOC]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.INVOICE_DOC]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.AUTH_DOC]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.FUNDER_DOC]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.TIMESHEET_DOC]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.RESOURCE_DOC]: [
        'image/png',
        'image/jpeg',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'video/mp4',
        'video/webm',
    ],
    [KeyPurpose.REPORT_FILE]: ['application/pdf', 'text/csv'],
    [KeyPurpose.GOAL_FILE]: ['image/png', 'image/jpeg', 'application/pdf'],
    [KeyPurpose.REMITTANCE_DOC]: ['application/pdf', 'text/plain'],
    [KeyPurpose.STAFF_DOC]: [
        'image/png',
        'image/jpeg',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],

    [KeyPurpose.CLINICAL_VIDEO]: ['video/mp4', 'video/webm', 'video/quicktime'],
    [KeyPurpose.TUTORIAL_VIDEO]: ['video/mp4', 'video/webm', 'video/quicktime'],
    [KeyPurpose.TUTORIAL_THUMBNAIL]: ['image/png', 'image/jpeg', 'image/webp'],
    [KeyPurpose.CHAT_ATTACHMENT]: [
        'image/png',
        'image/jpeg',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'video/mp4',
    ],
    [KeyPurpose.WISHLIST_ATTACHMENT]: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf', 'text/csv'],
    [KeyPurpose.QR]: ['image/png'],
};

/**
 * Per-purpose size cap in bytes. Must stay in sync with
 * @therapylake/package storage.constants.ts.
 */
export const MAX_BYTES_BY_PURPOSE = {
    [KeyPurpose.AVATAR]: 1 * 1024 * 1024,
    [KeyPurpose.SIGNATURE]: 5 * 1024 * 1024,
    [KeyPurpose.LOGO]: 5 * 1024 * 1024,
    [KeyPurpose.TUTORIAL_THUMBNAIL]: 5 * 1024 * 1024,
    [KeyPurpose.CHAT_ATTACHMENT]: 10 * 1024 * 1024,
    [KeyPurpose.WISHLIST_ATTACHMENT]: 10 * 1024 * 1024,

    [KeyPurpose.CLINICAL_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.CLAIM_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.FORM_AI_SOURCE]: 20 * 1024 * 1024,
    [KeyPurpose.INVOICE_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.AUTH_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.FUNDER_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.TIMESHEET_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.RESOURCE_DOC]: 60 * 1024 * 1024,
    [KeyPurpose.GOAL_FILE]: 50 * 1024 * 1024,
    [KeyPurpose.REMITTANCE_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.STAFF_DOC]: 50 * 1024 * 1024,
    [KeyPurpose.REPORT_FILE]: 100 * 1024 * 1024,
    [KeyPurpose.QR]: 100 * 1024,

    [KeyPurpose.CLINICAL_VIDEO]: 5 * 1024 * 1024 * 1024,
    [KeyPurpose.TUTORIAL_VIDEO]: 5 * 1024 * 1024 * 1024,
};

export function getDataForTable(givenData = '', isOpen, givenWidth) {
    const firstSize = isOpen ? 1850 : 1730;
    const firstLimit = isOpen ? 18 : 20;

    const secondSize = isOpen ? 1700 : 1640;
    const secondLimit = isOpen ? 12 : 14;

    const thirdSize = isOpen ? 1370 : 1345;
    const thirdLimit = isOpen ? 8 : 10;

    const initialLimit = isOpen ? 21 : 23;

    const tableData =
        givenWidth <= thirdSize
            ? getLimitedVal(givenData, thirdLimit)
            : givenWidth > thirdSize && givenWidth <= secondSize
              ? getLimitedVal(givenData, secondLimit)
              : givenWidth > secondSize && givenWidth <= firstSize
                ? getLimitedVal(givenData, firstLimit)
                : getLimitedVal(givenData, initialLimit);

    return tableData;
}

export function getGeneratingDataForTable(givenData = '', isOpen, givenWidth) {
    const firstSize = isOpen ? 1850 : 1825;
    const firstLimit = isOpen ? 12 : 14;

    const secondSize = isOpen ? 1450 : 1500;
    const secondLimit = isOpen ? 8 : 10;

    const initialLimit = isOpen ? 16 : 18;

    const tableData =
        givenWidth <= secondSize
            ? getLimitedVal(givenData, secondLimit)
            : givenWidth > secondSize && givenWidth <= firstSize
              ? getLimitedVal(givenData, firstLimit)
              : getLimitedVal(givenData, initialLimit);

    return tableData;
}

export function getModalDataForTable(givenData = '', givenWidth) {
    const firstSize = 1940;
    const firstLimit = 16;

    const secondSize = 1640;
    const secondLimit = 14;

    const thirdSize = 1345;
    const thirdLimit = 10;

    const initialLimit = 23;

    const tableData =
        givenWidth <= thirdSize
            ? getLimitedVal(givenData, thirdLimit)
            : givenWidth > thirdSize && givenWidth <= secondSize
              ? getLimitedVal(givenData, secondLimit)
              : givenWidth > secondSize && givenWidth <= firstSize
                ? getLimitedVal(givenData, firstLimit)
                : getLimitedVal(givenData, initialLimit);

    return tableData;
}
